import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/lib/supabase';
import { logActivity } from '@/lib/activityLogger';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DiscountPriceInput } from '@/components/ui/discount-price-input';
import { CategorySelector } from '@/components/ui/category-selector';
import { GenderSelector } from '@/components/ui/gender-selector';
import { SizesColorsSelector } from '@/components/ui/sizes-colors-selector';
import { CustomFlavorInput } from '@/components/ui/custom-flavor-input';
import { PackagingFields } from '@/components/ui/packaging-fields';
import { TieredPricingManager } from '@/components/ui/tiered-pricing-manager';
import { PricingModeToggle } from '@/components/ui/pricing-mode-toggle';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import type { PriceTier, WeightVariant } from '@/types';
import { ProductImageManager } from '@/components/product/ProductImageManager';
import { ProductWeightVariantsManager } from '@/components/product/ProductWeightVariantsManager';
import { uploadProductImages, saveProductImages } from '@/lib/productImageService';
import { PromotionalPhraseSelector } from '@/components/ui/promotional-phrase-selector';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useSubscriptionModal } from '@/contexts/SubscriptionModalContext';
import { useInventoryEnabled } from '@/hooks/useInventoryEnabled';
import VariantStockGrid, { VariantStockGridHandle } from '@/components/dashboard/VariantStockGrid';

const productSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  description: z.string().min(1, 'Descrição é obrigatória'),
  short_description: z.string().optional(),
  price: z.number().min(0, 'Preço deve ser maior ou igual a zero'),
  is_starting_price: z.boolean().default(false),
  featured_offer_price: z.number().optional(),
  featured_offer_installment: z.number().optional(),
  featured_offer_description: z.string().optional(),
  status: z.enum(['disponivel', 'vendido', 'reservado']).default('disponivel'),
  category: z.array(z.string()).default([]),
  brand: z.string().optional(),
  gender: z.string().optional(),
  condition: z.enum(['novo', 'usado', 'seminovo']).default('novo'),
  external_checkout_url: z.string().optional(),
  is_visible_on_storefront: z.boolean().default(true),
  colors: z.array(z.string()).default([]),
  sizes: z.array(z.string()).default([]),
  flavors: z.array(z.string()).default([]),
  has_tiered_pricing: z.boolean().default(false),
  track_inventory: z.boolean().default(true),
  stock_quantity: z.number().min(0).optional(),
  low_stock_threshold: z.number().min(0).default(5),
  weight_kg: z.number().min(0).optional(),
  height_cm: z.number().min(0).optional(),
  width_cm: z.number().min(0).optional(),
  length_cm: z.number().min(0).optional(),
  package_type: z.enum(['envelope', 'box', 'cylinder']).default('box'),
  diameter_cm: z.number().min(0).optional(),
  package_preset_id: z.string().uuid().optional().nullable(),
});

type ProductFormData = z.infer<typeof productSchema>;

type MediaItem = {
  id: string;
  url: string;
  file?: File;
  isFeatured: boolean;
  mediaType: 'image';
  fileHash?: string;
  visualFingerprint?: string;
  blobUrl?: string;
};

export default function CreateProductPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAddProduct } = usePlanLimits();
  const { openModal } = useSubscriptionModal();
  const { inventoryEnabled } = useInventoryEnabled();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedProductId, setSavedProductId] = useState<string | null>(null);
  const variantStockGridRef = useRef<VariantStockGridHandle>(null);
  const draftSaveInProgress = useRef(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const [pricingMode, setPricingMode] = useState<'simple' | 'tiered'>('simple');
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [isPriceTiersValid, setIsPriceTiersValid] = useState(true);
  const [isSizesColorsOpen, setIsSizesColorsOpen] = useState(false);
  const [isFlavorsOpen, setIsFlavorsOpen] = useState(false);
  const [isWeightVariantsOpen, setIsWeightVariantsOpen] = useState(false);
  const [isPackagingOpen, setIsPackagingOpen] = useState(false);
  const [isPromoPhraseOpen, setIsPromoPhraseOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [hasWeightVariants, setHasWeightVariants] = useState(false);
  const [weightVariants, setWeightVariants] = useState<WeightVariant[]>([]);
  const [images, setImages] = useState<MediaItem[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const userCurrency = (user?.currency as 'BRL' | 'USD' | 'EUR') || 'BRL';

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      title: '',
      description: '',
      short_description: '',
      price: 0,
      is_starting_price: false,
      featured_offer_price: undefined,
      featured_offer_installment: undefined,
      featured_offer_description: '',
      status: 'disponivel',
      category: [],
      brand: '',
      gender: '',
      condition: 'novo',
      external_checkout_url: '',
      is_visible_on_storefront: true,
      colors: [],
      sizes: [],
      flavors: [],
      has_tiered_pricing: false,
      track_inventory: true,
      stock_quantity: undefined,
      low_stock_threshold: 5,
      weight_kg: undefined,
      height_cm: undefined,
      width_cm: undefined,
      length_cm: undefined,
      package_type: 'box',
      diameter_cm: undefined,
      package_preset_id: null,
    },
  });

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (form.formState.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [form.formState.isDirty]);


  const saveDraft = async () => {
    if (!user?.id || savedProductId || draftSaveInProgress.current) return null;
    draftSaveInProgress.current = true;
    setSavingDraft(true);
    try {
      const data = form.getValues();
      const draftData = {
        user_id: user.id,
        title: data.title || 'Rascunho',
        description: data.description || '',
        short_description: data.short_description || '',
        price: data.price ?? 0,
        discounted_price: data.featured_offer_price || null,
        is_starting_price: data.is_starting_price ?? false,
        featured_offer_price: data.featured_offer_price || null,
        featured_offer_installment: data.featured_offer_installment || null,
        featured_offer_description: data.featured_offer_description || '',
        status: data.status ?? 'disponivel',
        category: data.category?.length > 0 ? data.category : ['Sem Categoria'],
        brand: data.brand || '',
        gender: data.gender || null,
        condition: data.condition ?? 'novo',
        featured_image_url: '',
        external_checkout_url: data.external_checkout_url || '',
        is_visible_on_storefront: false,
        colors: data.colors ?? [],
        sizes: data.sizes ?? [],
        flavors: data.flavors ?? [],
        has_tiered_pricing: false,
        pricing_mode: 'range',
        has_weight_variants: hasWeightVariants,
        track_inventory: true,
        stock_quantity: data.stock_quantity ?? 0,
        low_stock_threshold: data.low_stock_threshold ?? 5,
        weight_kg: data.weight_kg ?? null,
        height_cm: data.height_cm ?? null,
        width_cm: data.width_cm ?? null,
        length_cm: data.length_cm ?? null,
        package_type: data.package_type ?? 'box',
        diameter_cm: data.diameter_cm ?? null,
        package_preset_id: data.package_preset_id ?? null,
      };
      const { data: product, error } = await supabase
        .from('products')
        .insert(draftData)
        .select('id')
        .single();
      if (error) throw error;
      setSavedProductId(product.id);
      return product.id;
    } catch {
      toast.error('Não foi possível salvar o rascunho para controle de estoque');
      return null;
    } finally {
      setSavingDraft(false);
      draftSaveInProgress.current = false;
    }
  };

  // Auto-creates the draft as soon as the product has any color/size/flavor
  // combo and stock tracking is on, instead of only on an explicit toggle of
  // the "Controlar estoque" switch (which defaults to already-on, so most
  // merchants never fire that handler). Without this, the variant stock grid
  // never mounts during creation — merchants type quantities nowhere, hit
  // "Salvar" once, get redirected to the listings page with a product that
  // has zero stock, and only get a working grid the second time, editing it.
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (savedProductId || draftSaveInProgress.current) return;
      if (name !== 'colors' && name !== 'sizes' && name !== 'flavors') return;
      const hasVariants =
        (value.colors?.length ?? 0) > 0 ||
        (value.sizes?.length ?? 0) > 0 ||
        (value.flavors?.length ?? 0) > 0;
      if (hasVariants && value.track_inventory) {
        saveDraft();
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, savedProductId]);

  const onSubmit = async (data: ProductFormData) => {
    if (!user?.id) {
      toast.error('Usuário não autenticado');
      return;
    }

    if (!canAddProduct) {
      toast.error('Você atingiu o limite de produtos do Plano Free. Faça upgrade para continuar.');
      openModal(false, 'products');
      return;
    }

    if (images.length === 0) {
      toast.error('Adicione pelo menos uma imagem do produto');
      return;
    }

    if (pricingMode === 'tiered' && priceTiers.length === 0) {
      toast.error('Adicione pelo menos um nível de preço');
      return;
    }

    if (hasWeightVariants) {
      if (weightVariants.length < 2) {
        toast.error('Adicione ao menos 2 variações de peso');
        return;
      }
      const invalid = weightVariants.find(
        (v) => !v.label.trim() || v.price <= 0
      );
      if (invalid) {
        toast.error('Preencha rótulo e preço válidos em todas as variações de peso');
        return;
      }
      const labels = weightVariants.map((v) => v.label.trim().toLowerCase());
      if (new Set(labels).size !== labels.length) {
        toast.error('Existem rótulos duplicados nas variações de peso');
        return;
      }
    }

    if (pricingMode === 'tiered' && !isPriceTiersValid) {
      toast.error('Por favor, corrija os erros nos níveis de preço antes de salvar');
      return;
    }

    setLoading(true);
    try {
      // Products with a color/size/flavor grid get stock_quantity from
      // recalc_product_aggregate_stock (summed from product_variant_stock)
      // once the grid flushes below — writing the plain "Quantidade em
      // estoque" field here would just get overwritten by that recalc.
      const hasVariantGrid = data.colors.length > 0 || data.sizes.length > 0 || data.flavors.length > 0;

      const productData = {
        user_id: user.id,
        title: data.title,
        description: data.description,
        short_description: data.short_description || '',
        price: pricingMode === 'simple' ? data.price : 0,
        discounted_price: data.featured_offer_price || null,
        is_starting_price: data.is_starting_price,
        featured_offer_price: data.featured_offer_price || null,
        featured_offer_installment: data.featured_offer_installment || null,
        featured_offer_description: data.featured_offer_description || '',
        status: data.status,
        category: data.category.length > 0 ? data.category : ['Sem Categoria'],
        brand: data.brand || '',
        gender: data.gender || null,
        condition: data.condition,
        featured_image_url: '',
        external_checkout_url: data.external_checkout_url || '',
        is_visible_on_storefront: data.is_visible_on_storefront,
        colors: data.colors,
        sizes: data.sizes,
        flavors: data.flavors,
        has_tiered_pricing: pricingMode === 'tiered',
        pricing_mode: pricingMode === 'tiered' ? 'exact' : 'range',
        has_weight_variants: hasWeightVariants,
        track_inventory: data.track_inventory ?? false,
        ...(hasVariantGrid
          ? {}
          : { stock_quantity: data.track_inventory ? (data.stock_quantity ?? 0) : null }),
        low_stock_threshold: data.low_stock_threshold ?? 5,
        weight_kg: data.weight_kg ?? null,
        height_cm: data.height_cm ?? null,
        width_cm: data.width_cm ?? null,
        length_cm: data.length_cm ?? null,
        package_type: data.package_type ?? 'box',
        diameter_cm: data.diameter_cm ?? null,
        package_preset_id: data.package_preset_id ?? null,
      };

      let product: { id: string };
      if (savedProductId) {
        const { data: updated, error: productError } = await supabase
          .from('products')
          .update(productData)
          .eq('id', savedProductId)
          .select()
          .single();
        if (productError) throw productError;
        product = updated;
      } else {
        const { data: inserted, error: productError } = await supabase
          .from('products')
          .insert(productData)
          .select()
          .single();
        if (productError) throw productError;
        product = inserted;
      }

      // Flush any unsaved edits in the variant stock grid only after the
      // product's own colors/sizes/flavors are persisted above — the
      // aggregate-recalc RPC (and its client-side fallback) filters
      // product_variant_stock rows by what's CURRENTLY in products.colors/
      // sizes/flavors, so flushing before this insert/update would recalc
      // against a stale (often empty) list and silently drop quantities for
      // colors/sizes just added on this same save.
      const stockSaved = await variantStockGridRef.current?.flush();
      if (stockSaved === false) {
        toast.error('Produto salvo, mas houve erro ao salvar o estoque por variante. Tente salvar novamente.');
        return;
      }

      const filesToUpload = images
        .filter((img) => img.file)
        .map((img) => ({ file: img.file as File, isFeatured: img.isFeatured }));

      if (filesToUpload.length > 0) {
        setUploadingImages(true);
        const uploadedImages = await uploadProductImages(
          filesToUpload,
          user.id,
          product.id
        );

        if (uploadedImages.length > 0) {
          const featuredImage = uploadedImages.find((img) => img.is_featured);
          if (featuredImage) {
            await supabase
              .from('products')
              .update({ featured_image_url: featuredImage.url })
              .eq('id', product.id);
          }

          await saveProductImages(product.id, uploadedImages, user.id);
        }
        setUploadingImages(false);
      }

      if (hasWeightVariants && weightVariants.length > 0) {
        const variantRows = weightVariants.map((v, idx) => ({
          product_id: product.id,
          label: v.label.trim(),
          unit_value: Number(v.unit_value) || 0,
          unit_type: v.unit_type,
          price: Number(v.price) || 0,
          discounted_price: v.discounted_price ?? null,
          display_order: idx,
          shipping_weight_kg: v.shipping_weight_kg && v.shipping_weight_kg > 0 ? v.shipping_weight_kg : null,
        }));
        const { error: variantsError } = await supabase
          .from('product_weight_variants')
          .insert(variantRows);
        if (variantsError) throw variantsError;
      }

      if (pricingMode === 'tiered' && priceTiers.length > 0) {
        const sortedTiers = [...priceTiers].sort((a, b) => a.min_quantity - b.min_quantity);

        const tierRecords = sortedTiers.map((tier) => {
          const maxQuantity = tier.min_quantity;

          return {
            product_id: product.id,
            min_quantity: tier.min_quantity,
            max_quantity: maxQuantity,
            unit_price: tier.unit_price,
            discounted_unit_price: tier.discounted_unit_price,
          };
        });

        const { error: tiersError } = await supabase
          .from('product_price_tiers')
          .insert(tierRecords);

        if (tiersError) throw tiersError;
      }

      logActivity('product.create', `Criou o produto "${data.title}"`, 'product', product.id);
      toast.success('Produto criado com sucesso!');
      navigate('/dashboard/listings');
    } catch (error) {
      console.error('Error creating product:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Erro ao criar produto');
      }
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl page-title">Criar Produto</h1>
          <p className="text-muted-foreground">Adicione um novo produto à sua vitrine</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Produto *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Tênis Nike Air Max 90" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <FormControl>
                      <CategorySelector
                        value={field.value}
                        onChange={field.onChange}
                        userId={user?.id}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="brand"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Marca</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Nike" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gênero</FormLabel>
                      <FormControl>
                        <GenderSelector
                          value={field.value || ''}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Imagens do Produto</CardTitle>
            </CardHeader>
            <CardContent>
              <ProductImageManager
                images={images}
                onChange={setImages}
                maxImages={user?.max_images_per_product || 10}
                maxFileSize={5}
                availableColors={form.watch('colors') || []}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preços</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasWeightVariants && (
                <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground border">
                  O preço é definido em cada variação de peso. A vitrine exibirá "A partir
                  de" usando o menor preço cadastrado.
                </div>
              )}
              {!hasWeightVariants && (
              <>
              <PricingModeToggle
                isTieredPricing={pricingMode === 'tiered'}
                onModeChange={(useTieredPricing) => {
                  setPricingMode(useTieredPricing ? 'tiered' : 'simple');
                  form.setValue('has_tiered_pricing', useTieredPricing);
                }}
                hasSinglePriceData={form.watch('price') > 0}
                hasTieredPriceData={priceTiers.length > 0}
              />

              {pricingMode === 'simple' ? (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preço original do produto *</FormLabel>
                        <FormControl>
                          <CurrencyInput
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="R$ 0,00"
                            currency={userCurrency}
                          />
                        </FormControl>
                        <FormDescription>
                          Preço de venda do produto
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="featured_offer_price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preço promocional (deve ser menor que o preço original)</FormLabel>
                        <FormControl>
                          <DiscountPriceInput
                            value={field.value}
                            onChange={field.onChange}
                            originalPrice={form.watch('price')}
                            placeholder="R$ 0,00"
                            currency={userCurrency}
                          />
                        </FormControl>
                        <FormDescription className="text-muted-foreground">
                          Preço promocional opcional. Se preenchido, será exibido como oferta especial.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="is_starting_price"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Preço inicial</FormLabel>
                          <FormDescription>
                            Marque esta opção se o preço informado é um valor inicial ("A partir de")
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              ) : (
                <TieredPricingManager
                  tiers={priceTiers}
                  onChange={setPriceTiers}
                  onValidationChange={setIsPriceTiersValid}
                  currency={userCurrency}
                />
              )}
              </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Descrição</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição Completa *</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        content={field.value}
                        onChange={field.onChange}
                        placeholder="Descreva seu produto em detalhes..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Collapsible open={isSizesColorsOpen} onOpenChange={setIsSizesColorsOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Tamanhos e Cores
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isSizesColorsOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <SizesColorsSelector
                    colors={form.watch('colors')}
                    onColorsChange={(colors) => form.setValue('colors', colors)}
                    sizes={form.watch('sizes')}
                    onSizesChange={(sizes) => form.setValue('sizes', sizes)}
                    userId={user?.id}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <Collapsible open={isPromoPhraseOpen} onOpenChange={setIsPromoPhraseOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Frase Promocional
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isPromoPhraseOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="short_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <PromotionalPhraseSelector
                            value={field.value || ''}
                            onChange={field.onChange}
                            userId={user?.id}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <Collapsible open={isFlavorsOpen} onOpenChange={setIsFlavorsOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Sabores
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isFlavorsOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <CustomFlavorInput
                    value={form.watch('flavors')}
                    onChange={(flavors) => form.setValue('flavors', flavors)}
                    userId={user?.id}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <Collapsible open={isWeightVariantsOpen} onOpenChange={setIsWeightVariantsOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Variações de Peso
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isWeightVariantsOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <ProductWeightVariantsManager
                    enabled={hasWeightVariants}
                    onEnabledChange={setHasWeightVariants}
                    variants={weightVariants}
                    onChange={setWeightVariants}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {inventoryEnabled && (
            <Collapsible open={isInventoryOpen} onOpenChange={setIsInventoryOpen}>
              <Card>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        Estoque
                        <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                      </CardTitle>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isInventoryOpen ? 'transform rotate-180' : ''}`} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="track_inventory"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Controlar estoque deste produto</FormLabel>
                            <FormDescription>
                              Ative para gerenciar a quantidade disponível deste produto
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (checked) saveDraft();
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {form.watch('track_inventory') && (
                      <div className="space-y-4 pl-1">
                        {!(form.watch('colors')?.length > 0 || form.watch('sizes')?.length > 0 || form.watch('flavors')?.length > 0) && (
                          <FormField
                            control={form.control}
                            name="stock_quantity"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Quantidade em estoque *</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={0}
                                    placeholder="0"
                                    value={field.value ?? ''}
                                    onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}

                        <FormField
                          control={form.control}
                          name="low_stock_threshold"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Alertar quando atingir</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  placeholder="5"
                                  value={field.value ?? 5}
                                  onChange={(e) => field.onChange(e.target.value === '' ? 5 : Number(e.target.value))}
                                />
                              </FormControl>
                              <FormDescription>
                                Você será notificado quando o estoque atingir essa quantidade
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {(() => {
                          const colors = form.watch('colors');
                          const sizes = form.watch('sizes');
                          const flavors = form.watch('flavors');
                          const hasVariants = colors.length > 0 || sizes.length > 0 || flavors.length > 0;
                          if (!hasVariants) return null;
                          if (!savedProductId) {
                            return (
                              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground text-center">
                                {savingDraft
                                  ? 'Preparando grade de estoque por variante...'
                                  : 'Salve o rascunho para gerenciar o estoque por variante'}
                              </div>
                            );
                          }
                          return (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Estoque por variante</p>
                              <VariantStockGrid
                                ref={variantStockGridRef}
                                productId={savedProductId}
                                colors={colors}
                                sizes={sizes}
                                flavors={flavors}
                                lowStockThreshold={form.watch('low_stock_threshold') ?? 5}
                                performedBy={user?.id ?? ''}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          <Collapsible open={isPackagingOpen} onOpenChange={setIsPackagingOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Peso e Dimensões de Envio
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isPackagingOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                  <CardDescription>
                    Usado para calcular fretes automáticos. Salve uma embalagem para reutilizar as mesmas
                    medidas em outros produtos.
                  </CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <PackagingFields
                    value={{
                      package_type: form.watch('package_type'),
                      weight_kg: form.watch('weight_kg'),
                      height_cm: form.watch('height_cm'),
                      width_cm: form.watch('width_cm'),
                      length_cm: form.watch('length_cm'),
                      diameter_cm: form.watch('diameter_cm'),
                      package_preset_id: form.watch('package_preset_id'),
                    }}
                    onChange={(next) => {
                      form.setValue('package_type', next.package_type);
                      form.setValue('weight_kg', next.weight_kg);
                      form.setValue('height_cm', next.height_cm);
                      form.setValue('width_cm', next.width_cm);
                      form.setValue('length_cm', next.length_cm);
                      form.setValue('diameter_cm', next.diameter_cm);
                      form.setValue('package_preset_id', next.package_preset_id);
                    }}
                    userId={user?.id}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <Collapsible open={isConfigOpen} onOpenChange={setIsConfigOpen}>
            <Card>
              <CollapsibleTrigger className="w-full">
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      Configurações
                      <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground">opcional</span>
                    </CardTitle>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isConfigOpen ? 'transform rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="external_checkout_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Link Externo de Compra</FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormDescription>
                          Se preenchido, o botão de compra redirecionará para este link externo
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="is_visible_on_storefront"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Visível na Vitrine</FormLabel>
                          <FormDescription>
                            Mostrar este produto na sua vitrine pública
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          <div className="flex gap-2">
            <Button type="submit" disabled={loading || uploadingImages}>
              {loading || uploadingImages ? 'Criando e enviando imagens...' : 'Criar Produto'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
